// Carry Roll PracticeMode — virtual range, closest-to-pin, distance control (11.9).

import { useState } from 'react';
import { useStore } from '../../store';
import { useResult } from '../../store/selectors';

type Mode = 'range' | 'pin' | 'distance';

export function PracticeMode() {
  const [mode, setMode] = useState<Mode | null>(null);
  const [feedback, setFeedback] = useState('');
  const [targetM, setTargetM] = useState<number | null>(null);
  const result = useResult();
  const setLaunch = useStore((s) => s.setLaunch);

  const startMode = (m: Mode) => {
    setMode(m);
    setFeedback('');
    if (m === 'range') {
      setTargetM(null);
      setFeedback('Virtual range mode — adjust inputs and see live carry/total.');
    } else if (m === 'pin') {
      const pinM = 100 + Math.random() * 100;
      setTargetM(pinM);
      setFeedback(`Closest to pin: target ${Math.round(pinM)} m. Adjust your launch to match!`);
    } else if (m === 'distance') {
      const dist = 150 + Math.random() * 50;
      setTargetM(dist);
      setFeedback(`Distance control: hit ${Math.round(dist)} m carry. Adjust launch and spin.`);
    }
  };

  const checkResult = () => {
    if ((mode === 'pin' || mode === 'distance') && targetM != null) {
      const diff = Math.abs(result.carryM - targetM);
      const grade =
        diff < 3
          ? 'Perfect!'
          : diff < 8
            ? 'Close!'
            : diff < 15
              ? 'A bit off'
              : 'Try again';
      setFeedback(`${grade} — you hit ${Math.round(result.carryM)} m, target was ${Math.round(targetM)} m (off by ${Math.round(diff)} m).`);
    }
  };

  const randomize = () => {
    setLaunch({
      ballSpeed: 60 + Math.random() * 30,
      launchAngle: (10 + Math.random() * 8) * Math.PI / 180,
      spinRpm: 2000 + Math.random() * 3000,
    });
  };

  return (
    <div data-testid="practice-mode">
      <h3 className="subsection-title">
        Practice modes
      </h3>
      <div className="flex gap-1 mb-3">
        {(['range', 'pin', 'distance'] as const).map((m) => (
          <button
            key={m}
            type="button"
            className="btn"
            style={{
              fontWeight: mode === m ? 'var(--fw-bold)' : 'var(--fw-regular)',
              opacity: mode === m ? 1 : 0.6,
            }}
            onClick={() => startMode(m)}
          >
            {m === 'range' ? 'Virtual range' : m === 'pin' ? 'Closest to pin' : 'Distance control'}
          </button>
        ))}
      </div>
      {mode && (
        <div className="flex gap-2 mb-3">
          <button type="button" className="btn" onClick={randomize}>
            Randomize shot
          </button>
          {mode !== 'range' && (
            <button type="button" className="btn" onClick={checkResult}>
              Check result
            </button>
          )}
        </div>
      )}
      {feedback && (
        <p className="text-sm" data-testid="practice-feedback">
          {feedback}
        </p>
      )}
    </div>
  );
}
